import { capabilities } from '@/mocks/portfolio';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import SlotText from '@/components/feature/SlotText';

const PILLARS = [
  {
    no: '01',
    label: 'STRATEGY',
    sub: 'Business / Brand / CX / Communication',
    items: capabilities.strategy,
    note: '課題構造を捉え、価値と方向性を定義する。',
  },
  {
    no: '02',
    label: 'EXPERIENCE',
    sub: 'Service / Journey / Touchpoint / UX',
    items: capabilities.experience,
    note: '顧客体験の全体を設計し、接点を構造化する。',
  },
  {
    no: '03',
    label: 'IMPLEMENTATION',
    sub: 'Creative Direction / Web / Retail / Spatial',
    items: capabilities.implementation,
    note: '戦略を具体的なアウトプットまで実装する。',
  },
];

export default function WhatIDo() {
  const { ref, revealed } = useScrollReveal();

  return (
    <section id="positioning" className="py-20 md:py-28 bg-background-100">
      <div className="mx-auto max-w-[1280px] px-6 md:px-10">
        {/* Header */}
        <div
          ref={ref}
          className={`mb-14 transition-all duration-1000 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="flex items-center gap-2 mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />
            <span className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-500">
              Positioning / What I Do
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-6">
              <h2 className="font-heading fw-bold text-[clamp(32px,4.5vw,60px)] text-foreground-950 leading-[0.93] tracking-[-0.04em]">
                Strategy
                <br />
                <span className="text-foreground-500 fw-medium">to{' '}</span>
                <SlotText text="Implementation." as="span" className="marker-yellow" color="oklch(0.09 0.01 70)" scrambleColor="rainbow" />
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8 flex items-end">
              <p className="text-foreground-700 text-base leading-relaxed">
                事業・顧客課題を起点に価値を定義し、CX・ブランド・コミュニケーションを設計する。抽象論で終わらせず、Webから空間・リテール・映像・サービスまで、具体的な顧客接点として実装できることが強みです。
              </p>
            </div>
          </div>
        </div>

        {/* Flow */}
        <div
          className={`mb-12 flex flex-wrap items-center gap-2 md:gap-4 transition-all duration-1000 delay-100 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          {['Strategy', 'Experience', 'Implementation'].map((label, idx) => (
            <div key={label} className="flex items-center gap-2 md:gap-4">
              <span className="font-label text-[11px] tracking-[0.12em] uppercase px-4 py-2 whitespace-nowrap border border-foreground-950 text-foreground-950 hover:bg-primary-500 transition-colors cursor-pointer">
                <SlotText text={label} />
              </span>
              {idx < 2 && (
                <i className="ri-arrow-right-line text-foreground-400 w-4 h-4 flex items-center justify-center" />
              )}
            </div>
          ))}
        </div>

        {/* Three pillars */}
        <div
          className={`grid grid-cols-1 md:grid-cols-3 gap-px bg-background-300 border border-background-300 transition-all duration-1000 delay-200 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {PILLARS.map((pillar, idx) => (
            <div key={pillar.label} className="bg-background-50 p-8 md:p-10">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <span className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-400 block mb-1">
                    {pillar.no}
                  </span>
                  <p className="font-heading text-xl tracking-[-0.02em] text-foreground-950">
                    <SlotText text={pillar.label} as="span" />
                  </p>
                </div>
                <span
                  className={`w-2 h-2 rounded-full mt-1 flex-shrink-0 ${
                    idx === 0
                      ? 'bg-foreground-950'
                      : idx === 1
                      ? 'bg-secondary-400'
                      : 'bg-foreground-950'
                  }`}
                />
              </div>
              <p className="font-label text-[10px] tracking-[0.08em] uppercase text-foreground-400 mb-5">
                {pillar.sub}
              </p>
              <ul className="space-y-3 mb-6">
                {pillar.items.map((item) => (
                  <li key={item} className="text-sm text-foreground-800 leading-relaxed flex items-start gap-2">
                    <span className="w-1 h-1 rounded-full bg-foreground-400 mt-2 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="border-t border-background-300 pt-4">
                <p className="text-xs text-foreground-500 leading-relaxed">{pillar.note}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Differentiator callout */}
        <div
          className={`mt-10 border border-foreground-950 bg-background-50 p-6 md:p-8 transition-all duration-1000 delay-300 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <p className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-500 mb-2">
            What Makes This Different
          </p>
          <p className="font-heading text-lg md:text-2xl text-foreground-950 leading-tight tracking-[-0.02em]">
            <span className="marker-yellow">戦略を、言葉だけで終わらせない。</span>
          </p>
          <p className="mt-3 text-sm text-foreground-700 leading-relaxed max-w-3xl">
            グラフィック・空間・サイン・リテール・広告・Web・映像等の実装経験があるからこそ、上流の戦略設計が「具体的な顧客体験」として着地する。
          </p>
        </div>
      </div>
    </section>
  );
}